import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../config/api';
import { CheckCircle, Eye, X, Search, ClipboardCheck, XCircle, ArrowLeft } from 'lucide-react';
import toast from '../utils/toast';
import { formatDisplayDate } from '../utils/dateFormat';
import SearchBar from '../components/SearchBar';
import {
  masterHeaderCard,
  masterHeaderIcon,
  masterPageShell,
  masterPanelPadding,
  masterSearchInput,
  masterSearchWrap,
  masterTitle,
  masterTableData,
  masterToolbar,
  masterTableID,
  masterSubtitle,
  masterReadOnly,
  masterAccentButton
} from '../components/masterPageStyles';

const PurchaseOrderApproval = () => {
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    fetchDraftPOs();
  }, []);

  const fetchDraftPOs = async () => {
    setLoading(true);
    try {
      const res = await api.get('/purchase-orders');
      const allPos = res.data?.success ? res.data.data : res.data;
      const drafts = (allPos || []).filter(po => po.status === 'Draft');
      setPurchaseOrders(drafts);
    } catch (err) {
      toast.error('Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  };

const handleApprove = (id) => {
    toast((t) => (
      <div className="flex flex-col gap-3 min-w-[220px]">
        <p className="text-sm font-bold text-slate-800">Approve this Purchase Order?</p>
        <div className="flex gap-2 justify-end">
          <button 
            onClick={() => toast.dismiss(t.id)} 
            className="px-4 py-1.5 text-[10px] uppercase font-black bg-slate-100 text-slate-500 rounded-lg hover:bg-slate-200 transition-colors"
          >
            No
          </button>
          <button 
            onClick={async () => {
              toast.dismiss(t.id);
              try {
                await api.put(`/purchase-orders/${id}`, { status: 'Approved' });
                toast.success('Purchase Order Approved');
                setSelectedPO(null);
                fetchDraftPOs();
              } catch (err) {
                toast.error('Approval failed');
              }
            }} 
            className="px-4 py-1.5 text-[10px] uppercase font-black bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 shadow-sm transition-colors"
          >
            Yes, Approve
          </button>
        </div>
      </div>
    ), { 
      id: `approve-${id}`, // Prevents multiple popups for the same PO
      duration: 6000 
    });
  };

  const handleReject = (id) => {
    toast((t) => (
      <div className="flex flex-col gap-3 min-w-[220px]">
        <p className="text-sm font-bold text-slate-800">Reject this Purchase Order?</p>
        <div className="flex gap-2 justify-end">
          <button 
            onClick={() => toast.dismiss(t.id)} 
            className="px-4 py-1.5 text-[10px] uppercase font-black bg-slate-100 text-slate-500 rounded-lg hover:bg-slate-200 transition-colors"
          >
            No
          </button>
          <button 
            onClick={async () => {
              toast.dismiss(t.id);
              try {
                await api.put(`/purchase-orders/${id}`, { status: 'Rejected' });
                toast.error('Purchase Order Rejected');
                setSelectedPO(null);
                fetchDraftPOs();
              } catch (err) {
                toast.error('Rejection failed');
              }
            }} 
            className="px-4 py-1.5 text-[10px] uppercase font-black bg-rose-600 text-white rounded-lg hover:bg-rose-700 shadow-sm transition-colors"
          >
            Yes, Reject
          </button>
        </div>
      </div>
    ), { 
      id: `reject-${id}`, // Prevents multiple popups for the same PO
      duration: 6000 
    });
  };

  const filteredOrders = useMemo(() => {
    const sortedList = [...purchaseOrders].sort((a, b) => {
      const idA = String(a.po_number || a.po_id || a._id || '');
      const idB = String(b.po_number || b.po_id || b._id || '');
      return idB.localeCompare(idA, undefined, { numeric: true, sensitivity: 'base' });
    });

    const q = searchTerm.trim().toLowerCase();
    if (!q) return sortedList;

    return sortedList.filter((po) => {
      const poNumber = String(po?.po_number || '').toLowerCase();
      const poId = String(po?.po_id || '').toLowerCase();
      const supplierName = String(
        typeof po.supplier_id === 'object' ? po.supplier_id?.supplier_name : po.supplier_id || ''
      ).toLowerCase();

      return (
        poNumber.startsWith(q) ||
        poId.startsWith(q) ||
        supplierName.startsWith(q)
      );
    });
  }, [purchaseOrders, searchTerm]);

  const paginatedOrders = useMemo(() => {
    if (pageSize === -1) return filteredOrders;
    const start = (currentPage - 1) * pageSize;
    return filteredOrders.slice(start, start + pageSize);
  }, [filteredOrders, currentPage, pageSize]);

  return (
    <div className={`${masterPageShell} p-2`}>
      <header className={`${masterHeaderCard} mb-4`}>
        <div className="flex items-center gap-3">
          <div className={`${masterHeaderIcon} bg-amber-500 text-white`}><ClipboardCheck size={20} /></div>
          <div>
            <h1 className={masterTitle}>PO Approval</h1>
            <p className={masterSubtitle}>Pending Orders</p>
          </div>
        </div>
        {selectedPO && (
          <button onClick={() => setSelectedPO(null)} className="text-xs font-bold text-slate-500 flex items-center gap-1 uppercase hover:text-slate-800">
            <ArrowLeft size={14} /> Back
          </button>
        )}
      </header>

      <main className="mx-auto max-w-7xl">
        {!selectedPO ? (
          <div className="bg-white rounded-xl border border-slate-400 overflow-hidden shadow-sm">
           <div>
            <SearchBar 
              value={searchTerm} 
              onChange={(val) => {
                setSearchTerm(val);
                setCurrentPage(1);
              }} 
              placeholder="Search alphabetically by PO number, PO ID, or supplier..." 
              totalItems={filteredOrders.length} 
              currentPage={currentPage} 
              pageSize={pageSize} 
              onPageChange={setCurrentPage} 
              onPageSizeChange={setPageSize} 
            />
          </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-slate-50 border-b">
                    <th className={masterTableData}>S.No</th>
                    <th className={masterTableData}>PO Number</th>
                    <th className={masterTableData}>Supplier</th>
                    <th className={masterTableData}>Date</th>
                    <th className={masterTableData}>Amount</th>
                    <th className={masterTableData}>Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {paginatedOrders.length > 0 ? paginatedOrders.map((po, index) => (
                    <tr key={po._id} className="hover:bg-amber-50/40 font-bold text-slate-600">
                      <td className={masterTableData}>{index + 1}</td>
                      <td className={masterTableID}>{po.po_number || po.po_id}</td>
                      <td className={masterTableData}>{po.supplier_id?.supplier_name || po.supplier_id || 'N/A'}</td>
                      <td className={masterTableData}>{formatDisplayDate(po.po_date)}</td>
                      <td className={masterTableData}>
                         ₹{(po.items || []).reduce((sum, i) => sum + (Number(i.total_amount) || 0), 0).toFixed(2)}
                      </td>
                      <td className={masterTableData}>
                        <button onClick={() => setSelectedPO(po)} className="p-1.5 px-3 bg-sky-50 text-sky-700 hover:bg-sky-600 hover:text-white rounded-lg flex items-center gap-1 transition-all text-[10px] uppercase font-black">
                          <Eye size={14} /> Review
                        </button>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="6" className="p-20 text-center text-slate-300 uppercase text-xs">No records found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-400 overflow-hidden shadow-md">
            <div className={masterPanelPadding}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
                <DetailItem label="Supplier" value={selectedPO.supplier_id?.supplier_name || selectedPO.supplier_id} />
                <DetailItem label="PO Date" value={formatDisplayDate(selectedPO.po_date)} />
                <DetailItem label="Amount" value={`₹${selectedPO.items.reduce((sum, i) => sum + (Number(i.total_amount) || 0), 0).toFixed(2)}`} />
                <DetailItem label="Created By" value={selectedPO.created_by?.toUpperCase() || 'System'} />
              </div>

              <div className="rounded-xl border border-slate-200 overflow-hidden mb-8">
                <table className="w-full text-[11px]">
                  <thead className="bg-slate-100 uppercase font-black text-emerald-600 border-b">
                    <tr>
                      <th className="p-3 text-left">Medicine</th>
                      <th className="p-3 text-right">Qty</th>
                      <th className="p-3 text-right">Price</th>
                      <th className="p-3 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPO.items.map((item, idx) => (
                      <tr key={idx} className="font-bold text-slate-600 border-b">
                        <td className="p-3">{item.medicine_name?.toUpperCase() || '-'}</td>
                        <td className="p-3 text-right">{item.ordered_quantity}</td>
                        <td className="p-3 text-right">{item.purchase_price}</td>
                        <td className="p-3 text-right text-emerald-600">₹{Number(item.total_amount).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end gap-3 pt-6 border-t">
                <button onClick={() => handleReject(selectedPO._id)} className="px-8 py-2.5 bg-rose-50 text-rose-600 border border-rose-200 rounded-lg font-black text-[11px] uppercase flex items-center gap-2 hover:bg-rose-600 hover:text-white transition-all">
                  <XCircle size={16} /> Reject
                </button>
                <button onClick={() => handleApprove(selectedPO._id)} className={`${masterAccentButton} px-10 py-2.5 flex items-center gap-2`}>
                  <CheckCircle size={16} /> Approve
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

const DetailItem = ({ label, value }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{label}</label>
    <div className="text-sm font-bold text-slate-700">{value || '-'}</div>
  </div>
);

export default PurchaseOrderApproval;